import Box from '@mui/material/Box'
import { Button, Stack, Typography } from '@mui/material'
import {
  MechanicalEngineeringIcon,
  StructuralEngineeringIcon,
  ElectricalEngineeringIcon,
  PlumbingEngineeringIcon,
} from '../Svgs'
import { AssignDisciplineNames, AssignStatus } from '../../models/Review'
import {
  getCategoryColor,
  getProjectStatus,
  getServiceName,
  getStatusColor,
} from '../../utils/ComponentsUtil'
import AssignedEngineerComponent from './AssignedEngineerComponent'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { assignProjectToOrganization } from '../../services/reviewService'
import { showOneTimeServicePaymentDialog } from '../../actions/organization'
import { setSelectedOneTimePaymentProductName } from '../../actions/payment'

interface AssignEngineerProps {}

const AssignEngineerComponent: React.FC<AssignEngineerProps> = () => {
  const dispatch = useDispatch<any>()
  const file = useSelector(
    (state: RootState) => state.organization.selectedFile,
  )
  const assignedItems = useSelector(
    (state: RootState) => state.review.assignedItems,
  )
  const engineerOrganization = useSelector(
    (state: RootState) => state.review.engineerOrganization,
  )

  const paidServices = useSelector(
    (state: RootState) => state.payment.filePaidServices,
  )

  const disciplines = Object.values(AssignDisciplineNames)
  const sortedDisciplinesByUser = [...disciplines].sort((a, b) =>
    assignedItems.find((item) => item.discipline === a)?.user !== null ? -1 : 1,
  )

  const getAssignedDiscipline = (discipline: AssignDisciplineNames) => {
    return assignedItems.find((item) => item.discipline === discipline)
  }

  const getCategoryIcon = (category: AssignDisciplineNames) => {
    switch (category) {
      case AssignDisciplineNames.Mechanical:
        return (
          <MechanicalEngineeringIcon
            color={getCategoryColor(AssignDisciplineNames.Mechanical)}
          />
        )
      case AssignDisciplineNames.Electrical:
        return (
          <ElectricalEngineeringIcon
            color={getCategoryColor(AssignDisciplineNames.Electrical)}
          />
        )
      case AssignDisciplineNames.Structural:
        return (
          <StructuralEngineeringIcon
            color={getCategoryColor(AssignDisciplineNames.Structural)}
          />
        )
      case AssignDisciplineNames.Plumbing:
        return (
          <PlumbingEngineeringIcon
            color={getCategoryColor(AssignDisciplineNames.Plumbing)}
          />
        )
    }
  }

  const handleAssignClick = (discipline: AssignDisciplineNames) => {
    if (!!!file) {
      console.error('Error: file id is missed in assign request')
      return
    }

    const service = getServiceName(discipline)

    if (paidServices.includes(service)) {
      if (engineerOrganization) {
        dispatch(
          assignProjectToOrganization({
            discipline,
            discipline_status: AssignStatus.NotAssigned,
            status: AssignStatus.NotAssigned,
            organization_id: Number(engineerOrganization?.id), //engineering organization Id
            file_id: file.id,
          }),
        )
      }
    } else {
      dispatch(setSelectedOneTimePaymentProductName(service))
      dispatch(showOneTimeServicePaymentDialog(true))
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack gap={'18px'}>
        <Typography
          sx={{
            color: '#797979',
            fontSize: '0.76rem',
            lineHeight: 'normal',
            fontWeight: 500,
          }}
        >
          Engineers Type
        </Typography>
        {sortedDisciplinesByUser.map((discipline, index) => {
          const assignedItem = getAssignedDiscipline(discipline)
          return (
            <Box
              key={index}
              gap={'0.5rem'}
              sx={{
                background: '#F5F5F5',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                borderRadius: '20px',
                padding: '17px 27px 17px 22px',
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: '15px',
                    width: '100%',
                    alignItems: 'center',
                  }}
                >
                  {getCategoryIcon(discipline)}
                  <Typography
                    sx={{
                      color: '#06004A',
                      fontSize: '0.94rem',
                      lineHeight: 'normal',
                      fontWeight: 500,
                      textTransform: 'capitalize',
                    }}
                  >
                    {`${discipline.toString().toLowerCase()}`}
                  </Typography>
                </Box>
                {!!!assignedItem ||
                (!!assignedItem &&
                  (!!!assignedItem.user_id ||
                    assignedItem.discipline_status !==
                      AssignStatus.InProgress)) ? (
                  <Box sx={{ display: 'flex', gap: '18px' }}>
                    <Button
                      disableRipple={!!!assignedItem}
                      variant={!!!assignedItem ? 'contained' : 'outlined'}
                      onClick={
                        !!!assignedItem
                          ? () => handleAssignClick(discipline)
                          : () => {}
                      }
                      sx={{
                        textTransform: 'capitalize',
                        fontSize: '0.76rem',
                        borderRadius: '4px',
                        minWidth: '110px',
                        background: !!!assignedItem
                          ? getCategoryColor(discipline)
                          : 'transparent',
                        border: !!!assignedItem
                          ? 'none'
                          : `1px solid ${getCategoryColor(discipline)}`,
                        color: !!!assignedItem
                          ? '#fff'
                          : getCategoryColor(discipline),
                        pointerEvents: !!!assignedItem ? 'auto' : 'none',
                      }}
                    >
                      {!!!assignedItem ? 'Assign' : 'Request Sent'}
                    </Button>
                  </Box>
                ) : (
                  <Box display='flex'>
                    <Typography
                      sx={{
                        color: getStatusColor(
                          null,
                          assignedItem.discipline_status,
                          0,
                        ),
                        fontSize: '0.7rem',
                        lineHeight: '1.5rem',
                        textAlign: 'end',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {getProjectStatus(
                        assignedItem.discipline_status,
                      ).toUpperCase()}
                    </Typography>
                  </Box>
                )}
              </Box>
              {!!assignedItem &&
                !!assignedItem.user_id &&
                assignedItem.discipline_status === AssignStatus.InProgress && (
                  <AssignedEngineerComponent discipline={assignedItem} />
                )}
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}

export default AssignEngineerComponent
